import classNames from 'classnames';
import DOMPurify from 'dompurify';
import type { ReactNode } from 'react';
import { isEditorHtml } from '~/utils/editorHtml';

// The entities RichTextArea's escape-on-hydration path (and DOMPurify) can
// produce. A legacy plain-text row that gets edited once and saved again is
// stored with these -- e.g. "Use <Epic> for charting" round-trips through
// RichTextArea as "Use &lt;Epic&gt; for charting" -- so the plain-text
// branch below must decode them back before rendering, or the user sees the
// literal entity text instead of the character it stands for.
const BASIC_ENTITIES: Record<string, string> = {
  '&lt;': '<',
  '&gt;': '>',
  '&amp;': '&',
  '&quot;': '"',
  '&#39;': "'",
  '&nbsp;': ' ',
};
const decodeBasicEntities = (value: string): string =>
  value.replace(/&(lt|gt|amp|quot|#39|nbsp);/g, (match) => BASIC_ENTITIES[match] ?? match);

// Non-increasing sizes down the heading hierarchy (h1 largest, h6 smallest)
// regardless of container width -- h3-h6 must never render larger than h2.
// [MCV-1955] h3 is `text-[1rem]`, not `text-base`: here `text-base` is the near-white colour token, which painted h3 text invisible.
const LIST_STYLE_CLASSES =
  '[&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_p]:mb-2 [&_a]:underline ' +
  '[&_h1]:text-xl [&_h1]:font-semibold [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:text-[1rem] [&_h3]:font-semibold ' +
  '[&_h4]:text-sm [&_h4]:font-semibold [&_h5]:text-sm [&_h5]:font-semibold [&_h6]:text-sm [&_h6]:font-semibold ' +
  '[&_pre]:whitespace-pre-wrap [&_pre]:font-mono [&_pre]:text-sm';

export interface RichTextContentProps {
  /** Raw stored value: legacy plain text, or DOMPurify-sanitized HTML from RichTextArea. */
  value?: string | null;
  className?: string;
  /** Shown when the value is empty or whitespace-only. */
  fallback?: ReactNode;
  dataCy?: string;
}

/**
 * Renders a description field that may be legacy plain text (no HTML tags)
 * or sanitized HTML produced by `RichTextArea`. Plain text renders exactly
 * as it always has (`whitespace-pre-wrap`), so pre-existing rows are
 * unaffected; HTML is re-sanitized defensively before being injected.
 */
export const RichTextContent = ({ value, className, fallback, dataCy }: RichTextContentProps) => {
  const trimmed = typeof value === 'string' ? value.trim() : '';

  if (!trimmed) {
    return (
      <p className={className} data-cy={dataCy}>
        {fallback}
      </p>
    );
  }

  if (!isEditorHtml(trimmed)) {
    return (
      <p className={classNames('whitespace-pre-wrap', className)} data-cy={dataCy}>
        {decodeBasicEntities(trimmed)}
      </p>
    );
  }

  const sanitized = DOMPurify.sanitize(value as string, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'b',
      'strong',
      'i',
      'em',
      'u',
      's',
      'ul',
      'ol',
      'li',
      'a',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'pre',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });

  return (
    <div
      className={classNames(LIST_STYLE_CLASSES, className)}
      data-cy={dataCy}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
};
